import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Leaf, Loader2, KeyRound, Mail, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import MfaStep from "@/components/MfaStep";

export default function Login() {
  const { login, user, error, setUser } = useAuth();
  const [mfa, setMfa] = useState(null); // { mode, token }
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // forgot-password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  // ?next=/path lets the 401 interceptor send users back to where they were.
  const nextPath = (() => {
    const raw = new URLSearchParams(location.search).get("next");
    if (!raw) return "/app";
    // Only allow same-origin internal paths to avoid open-redirect.
    return raw.startsWith("/") && !raw.startsWith("//") ? raw : "/app";
  })();

  useEffect(() => { if (user) navigate(nextPath); }, [user, navigate, nextPath]);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.mfa) setMfa({ mode: res.mfa, token: res.mfaToken });
    else if (res.ok) navigate(nextPath);
  }

  async function onForgot(e) {
    e.preventDefault();
    if (!forgotEmail.trim() || !forgotEmail.includes("@")) {
      toast.error("Inserisci un'email valida");
      return;
    }
    setForgotSending(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      setForgotDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally { setForgotSending(false); }
  }

  function openForgot() {
    setForgotEmail(email);
    setForgotDone(false);
    setForgotOpen(true);
  }

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1576502200916-3808e07386a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGNvbG9yZnVsJTIwY2xlYW4lMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3OTYxNTMxM3ww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-viva-900/85 via-viva-700/60 to-viva-500/50 mix-blend-multiply" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <img src="/viva_logo.png" alt="VIVA ESG" className="w-24 h-24 object-contain bg-white/15 backdrop-blur rounded-xl p-1 border border-white/20" />
            <div>
              <div className="font-heading font-bold text-xl leading-tight">VIVA ESG</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/80">Rating Platform</div>
            </div>
          </div>
          <div className="max-w-xl">
            <h1 className="font-heading font-bold text-4xl xl:text-5xl tracking-tight">
              Misura, valuta e certifica la sostenibilità della tua azienda.
            </h1>
            <p className="mt-5 text-white/85 leading-relaxed text-base xl:text-lg">
              La piattaforma VIVA ESG calcola il rating ambientale, sociale e di governance secondo
              standard verificati e benchmark settoriali.
            </p>
            <div className="mt-8 flex gap-2">
              {["V1","V2","V3","V4","V5","V6","V7"].map((b, i) => (
                <div
                  key={b}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white/15 backdrop-blur border border-white/20"
                  style={{ opacity: 0.6 + i * 0.06 }}
                >
                  {b}
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-white/60">
            © 2026 VIVA ESG — Sustainable Rating Italia
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/viva_logo.png" alt="VIVA ESG" className="w-20 h-20 object-contain" />
            <div className="font-heading font-bold text-lg">VIVA ESG</div>
          </div>

          <a
            href="/"
            data-testid="login-back-home"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-viva-700 mb-4"
          >
            ← Torna alla home
          </a>

          <div className="mb-8">
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-viva-600 mb-2">Accesso riservato</div>
            <h2 className="font-heading text-3xl font-bold text-slate-900">Benvenuto</h2>
            <p className="text-slate-500 mt-2">Inserisci le tue credenziali per accedere alla piattaforma.</p>
          </div>

          {mfa ? (
            <MfaStep
              mode={mfa.mode}
              mfaToken={mfa.token}
              onSuccess={(u) => { setUser(u); navigate(nextPath); }}
              onCancel={() => { setMfa(null); setPassword(""); }}
            />
          ) : (
          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="nome@azienda.it"
                data-testid="login-email-input"
                className="mt-1.5 h-11 rounded-lg border-slate-200 focus-visible:ring-viva-500/20 focus-visible:border-viva-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</Label>
                <button
                  type="button"
                  onClick={openForgot}
                  data-testid="forgot-password-link"
                  className="text-[11px] font-semibold text-viva-700 hover:text-viva-900 hover:underline"
                >
                  Ho dimenticato la password
                </button>
              </div>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  className="h-11 rounded-lg border-slate-200 focus-visible:ring-viva-500/20 focus-visible:border-viva-500 pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  title={showPassword ? "Nascondi password" : "Mostra password"}
                  data-testid="login-password-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-lg bg-viva-500 hover:bg-viva-600 text-white font-semibold"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedi"}
            </Button>
          </form>
          )}
        </div>
      </div>

      {/* Forgot password dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-viva-600" /> Recupero password
            </DialogTitle>
          </DialogHeader>
          {forgotDone ? (
            <div className="space-y-4 py-2" data-testid="forgot-success">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-viva-50 border border-viva-200">
                <CheckCircle2 className="w-7 h-7 text-viva-600 shrink-0" />
                <div className="text-sm text-slate-700">
                  Se l&apos;indirizzo <b>{forgotEmail}</b> è registrato, riceverai a breve
                  un&apos;email con una <b>password temporanea</b> e le istruzioni per accedere.
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Controlla anche la cartella <b>Spam</b>. Se non ricevi l&apos;email entro pochi minuti,
                contatta <a href="mailto:tecnico@vivaesg.it" className="text-viva-700 font-semibold">tecnico@vivaesg.it</a>.
              </p>
              <DialogFooter>
                <Button
                  onClick={() => setForgotOpen(false)}
                  data-testid="forgot-close-button"
                  className="bg-viva-500 hover:bg-viva-600 text-white"
                >
                  Chiudi
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={onForgot} className="space-y-4">
              <p className="text-sm text-slate-600">
                Inserisci l&apos;indirizzo email del tuo account. Ti invieremo una password temporanea
                con cui potrai accedere e scegliere una nuova password personale.
              </p>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email account</Label>
                <div className="relative mt-1.5">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input
                    type="email"
                    autoFocus
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="nome@azienda.it"
                    data-testid="forgot-email-input"
                    className="h-11 pl-9 rounded-lg"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button" variant="ghost"
                  onClick={() => setForgotOpen(false)}
                  data-testid="forgot-cancel-button"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={forgotSending}
                  data-testid="forgot-submit-button"
                  className="bg-viva-500 hover:bg-viva-600 text-white"
                >
                  {forgotSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Invia password temporanea
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
